import React, { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { canExport } from "@/lib/permissions";
import { AgentMultiSelect } from "@/components/AgentMultiSelect";
import { DEMO_COMPLETED_SUB_STATUSES } from "@/lib/pipelineStages";
import {
  TrendingUp, DollarSign, Target, FileText, Download,
  Printer, Sparkles, Loader2, Users, Calendar,
  ChevronRight, ChevronDown, FileSpreadsheet, ArrowUpRight, Activity,
  X, AlertTriangle, Zap, Clock, CalendarDays, RefreshCw
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart as RechartPie, Pie, Cell, Legend,
} from "recharts";
// CommsDashboard is loaded as a lazy chunk for the Comms tab.
const CommsDashboard = lazy(() => import("@/pages/CommsDashboard"));
import RcAttributionBanner from "@/components/RcAttributionBanner";
// AdminRepActivity replaces the standalone /admin/rep-activity route — lives
// inside Reports as an admin-gated tab so all oversight is in one place.
const AdminRepActivity = lazy(() => import("@/pages/AdminRepActivity"));
// RC Connections (Bundle 2 ROI tile) — per-rep RingCentral OAuth status,
// adoption %, and last-7d dialing stats.
import RcConnectionsPanel from "@/components/rc/RcConnectionsPanel";
import ClientDemoActivity from "@/components/reports/ClientDemoActivity";
// Training Completion (Iter 155) — joins learning_progress × users to
// show managers who's caught up vs. lagging on the EMR / CCM / RPM /
// APCM / Quick-Reference courses.
const TrainingCompletionPanel = lazy(() => import("@/components/TrainingCompletionPanel"));
// AI Suggestion Acceptance (Phase 1) — aggregates lead_ai_suggestions
// to show acceptance / edit / rejection rates per rep.
const AiAcceptancePanel = lazy(() => import("@/components/AiAcceptancePanel"));
// Agent Performance Radar (Iter 165 — Danny Phase 3.3) — wires the
// 6-dim call data into a radar + 30-day trend + AI personal coaching
// suggestions with Learning Path deep-links.
const AgentPerformanceRadar = lazy(() => import("@/components/AgentPerformanceRadar"));

/* ─── Apple-inspired palette ─── */
const AP = {
  blue: "#0071e3",
  indigo: "#5856d6",
  purple: "#af52de",
  pink: "#ff2d55",
  red: "#ff3b30",
  orange: "#ff9500",
  yellow: "#ffcc00",
  green: "#34c759",
  teal: "#5ac8fa",
  mint: "#00c7be",
  gray: "#8e8e93",
  navy: "#1b365D",
};
const STAGE_COLORS = {
  "New Leads": AP.blue,
  "Contacted": AP.teal,
  "Demo": AP.indigo,
  "Proposal": AP.orange,
  "Decision": AP.mint,
  "Closed Won": AP.green,
  "Implementation": "#1a5c8a",
  "Inactive / Lost": AP.gray,
};
const PIE_PALETTE = [AP.blue, AP.green, AP.indigo, AP.orange, AP.pink, AP.teal, AP.purple, AP.gray];
const RATING_MAP = { Hot: AP.red, Warm: AP.orange, Cold: AP.teal, Dormant: AP.gray };

/* ─── Stage sync — mirrors Leads.jsx COLUMNS exactly ─── */
const PIPELINE_STAGES = [
  { id: "new_lead", label: "New Leads", color: AP.blue, dbStages: ["new_lead"] },
  { id: "contacted", label: "Contacted", color: AP.teal, dbStages: ["contacted", "follow_up"] },
  { id: "demo", label: "Demo", color: AP.indigo, dbStages: ["demo_scheduled", "demo_confirmed", "demo_completed"] },
  { id: "proposal", label: "Proposal", color: AP.orange, dbStages: ["proposal", "quote_sent"] },
  { id: "decision", label: "Decision", color: AP.mint, dbStages: ["decision", "negotiation"] },
  { id: "closed", label: "Closed Won", color: AP.green, dbStages: ["won", "closed_won", "contract"] },
  { id: "implementation", label: "Implementation", color: "#1a5c8a", dbStages: ["implementation"] },
  { id: "inactive", label: "Inactive / Lost", color: AP.gray, dbStages: ["inactive", "stalled", "lost", "closed_lost"] },
];

const DATE_PRESETS = [
  { label: "All Time", from: null, to: null },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "This Quarter", quarter: true },
  { label: "Last 90 days", days: 90 },
  { label: "This Year", year: true },
];

function getDateRange(preset) {
  if (!preset.days && !preset.quarter && !preset.year) return { from: null, to: null };
  const now = new Date();
  // Iter 219.1 (Aug 2026) — LOCAL calendar day for `to`. Reports page
  // presets previously used `toISOString().split("T")[0]` which
  // returned UTC calendar day; for US-timezone users in the evening
  // that pointed the window at TOMORROW and today's data was missing.
  // `from` for numeric presets keeps the rolling-24h subtraction
  // (that's the intent — "last 7 days" = 7 * 24h ago, not "7 calendar
  // days ago at local midnight") but anchors the LOCAL day for the
  // upper bound.
  const _iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const to = _iso(now);
  if (preset.days) {
    const from = _iso(new Date(now.getTime() - preset.days * 86400000));
    return { from, to };
  }
  if (preset.quarter) {
    const qMonth = Math.floor(now.getMonth() / 3) * 3;
    const from = _iso(new Date(now.getFullYear(), qMonth, 1));
    return { from, to };
  }
  if (preset.year) {
    return { from: `${now.getFullYear()}-01-01`, to };
  }
  return { from: null, to: null };
}

/* ─── CSV / Print helpers ─── */
function downloadCSV(rows, filename) {
  if (!rows?.length) { toast.error("No data to export"); return; }
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${filename}.csv`; a.click();
  toast.success(`${filename}.csv downloaded`);
}

function printReport(title, rows) {
  if (!rows?.length) { toast.error("No data"); return; }
  const headers = Object.keys(rows[0]);
  const html = `<!DOCTYPE html><html><head><title>${title}</title><style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',sans-serif;padding:40px;color:#1d1d1f}
    h1{font-size:22px;margin-bottom:4px;font-weight:700} h2{font-size:12px;color:#86868b;margin-bottom:24px;font-weight:400}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#1b365D;color:#fff;padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px}
    td{padding:7px 12px;border-bottom:1px solid #f5f5f7} tr:nth-child(even){background:#f5f5f7}
    .footer{margin-top:24px;font-size:10px;color:#86868b;text-align:center;border-top:1px solid #d2d2d7;padding-top:12px}
  </style></head><body>
  <h1>${title}</h1><h2>Enable Healthcare CRM &middot; ${new Date().toLocaleString()}</h2>
  <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>
  ${rows.map(r => `<tr>${headers.map(h => `<td>${r[h] ?? ""}</td>`).join("")}</tr>`).join("")}
  </tbody></table><div class="footer">Enable Healthcare &middot; ehiehr.com</div></body></html>`;
  const w = window.open("", "_blank"); w.document.write(html); w.document.close(); w.print();
}

/* ─── Custom Tooltip ─── */
function CTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(255,255,255,0.96)", backdropFilter: "blur(16px)", border: "0.5px solid #d2d2d7", borderRadius: 10, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
      {label && <div style={{ fontSize: 10, fontWeight: 600, color: "#1d1d1f", marginBottom: 3 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 11, color: p.color || "#1d1d1f", display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: p.color || AP.blue }} />
          {p.name}: <strong>{typeof p.value === "number" && p.value > 999 ? `$${p.value.toLocaleString()}` : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ─── KPI Card (clickable) ─── */
function KPI({ label, value, sub, subColor, icon: Icon, color, onClick }) {
  return (
    <button onClick={onClick} data-testid={`kpi-${label.replace(/\s/g, "-").toLowerCase()}`}
      style={{
        textAlign: "left", padding: "16px 18px", borderRadius: 14, border: "0.5px solid #d2d2d7",
        background: "#fff", cursor: onClick ? "pointer" : "default",
        fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 14,
        transition: "all 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 4px 16px ${color}20`; } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#d2d2d7"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon style={{ width: 20, height: 20, color }} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px", color: "#1d1d1f" }}>{value}</div>
        <div style={{ fontSize: 10, color: "#86868b", fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 9, fontWeight: 600, color: subColor || "#86868b", marginTop: 1 }}>{sub}</div>}
      </div>
    </button>
  );
}

/* ─── Meeting Drill Modal (Aug 2026) ───
   Opens when the manager clicks a Demos Scheduled or Demos Completed cell
   on the Rep Performance table. Shows the exact list of meetings + demo
   bookings that fed the count with jump-to-lead links.  */
function DemoDrillModal({ rep, bucket, rows, loading, onClose }) {
  const scheduled = bucket === "scheduled";
  const accent = scheduled ? "#0891b2" : "#15803d";
  const emptyMsg = scheduled
    ? "No scheduled demos in this window."
    : "No completed demos in this window.";

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
               display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      data-testid="demo-drill-modal"
    >
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#fff", borderRadius: 12, maxWidth: 940, width: "100%",
                    maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column",
                    boxShadow: "0 20px 40px -12px rgba(0,0,0,0.25)" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #e2e8f0",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      background: "#fafafa" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8",
                          textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {rep?.name || rep?.email} — Demos {scheduled ? "Scheduled" : "Completed"}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: accent, marginTop: 3 }}>
              {loading ? "…" : `${rows.length} demo${rows.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "0.5px solid #e2e8f0", borderRadius: 8,
                     padding: "6px 12px", fontSize: 11, cursor: "pointer", color: "#475569",
                     fontFamily: "inherit" }}
            data-testid="demo-drill-close"
          >Close</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: "0 6px" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
              Loading demos…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              {emptyMsg}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", fontWeight: 700,
                             textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>When</th>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>Lead / Company</th>
                  <th style={{ padding: "10px 14px", textAlign: "left" }}>Status</th>
                  {scheduled
                    ? <th style={{ padding: "10px 14px", textAlign: "left" }}>Source</th>
                    : <th style={{ padding: "10px 14px", textAlign: "left" }}>Outcome</th>}
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const when = scheduled
                    ? (r.scheduled_at || r.created_at || "")
                    : (r.completed_at || r.updated_at || "");
                  const whenLabel = when
                    ? new Date(when).toLocaleString(undefined,
                        { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                    : "—";
                  const statusColor = r.status === "completed" ? "#15803d"
                                     : r.status === "no_show" ? "#dc2626"
                                     : r.status === "canceled" || r.status === "cancelled" ? "#94a3b8"
                                     : r.status === "confirmed" ? "#0891b2"
                                     : "#475569";
                  return (
                    <tr key={r.id} style={{ borderTop: "0.5px solid #e2e8f0" }}
                        data-testid={`demo-drill-row-${r.id}`}>
                      <td style={{ padding: "10px 14px", color: "#0f172a" }}>{whenLabel}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>
                          {r.lead_name || r.contact_name || "Untitled"}
                        </div>
                        {r.contact_name && r.lead_name && r.contact_name !== r.lead_name && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.contact_name}</div>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10,
                                       background: statusColor + "18", color: statusColor,
                                       fontSize: 10, fontWeight: 600, textTransform: "capitalize" }}>
                          {(r.status || "").replace("_", " ")}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", color: "#475569", fontSize: 11 }}>
                        {scheduled
                          ? (r.source === "demo_booking" ? "Demo Bookings" : "Meetings")
                          : (r.outcome ? r.outcome.replace(/_/g, " ") : "—")}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        {r.lead_id ? (
                          <a href={`/lead-focus/${r.lead_id}`} target="_blank" rel="noreferrer"
                             style={{ color: "#5B6CFF", fontWeight: 600, fontSize: 11, textDecoration: "none" }}
                             data-testid={`demo-drill-lead-link-${r.id}`}
                             onClick={(e) => e.stopPropagation()}>
                            View lead →
                          </a>
                        ) : (
                          <span style={{ color: "#cbd5e1", fontSize: 11 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Drill-down Lightbox ─── */
function DrillDownModal({ title, leads, color, onClose, rep, drillDateFrom, drillDateTo }) {
  const [taskTitle, setTaskTitle] = useState("");
  // Iter 140.9 (Peter 65095bb7) — Rep drill-down now fetches the real
  // call activity for the selected rep and renders it as an inline
  // table so the manager doesn't have to leave /reports.
  const [callData, setCallData] = useState(null); // { rows, summary, loading, error, window_days }
  // Iter 140.13 — expandable notes per call + real AI summary.
  const [expandedRow, setExpandedRow] = useState(null); // call row id
  const [aiSummary, setAiSummary] = useState(null); // { headline, highlights, risks, next_actions, stats, loading, error }
  const { token } = useAuth();
  // Iter 341 — build the params the backend needs. When Reports has an
  // active date filter, honor it exactly; otherwise fall back to the
  // legacy 30-day window so existing behavior is preserved for the
  // default (no-filter) state.
  const drillParams = drillDateFrom && drillDateTo
    ? { date_from: drillDateFrom, date_to: drillDateTo }
    : { days: 30 };
  useEffect(() => {
    if (!rep?.id || !token) return;
    let cancelled = false;
    setCallData({ loading: true });
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/rep-activity/${rep.id}/calls`, {
          params: { ...drillParams, limit: 300 },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCallData({ loading: false, ...res.data });
      } catch (e) {
        if (!cancelled) setCallData({ loading: false, error: true, message: e?.response?.data?.detail || "Couldn't load call activity" });
      }
    })();
    return () => { cancelled = true; };
  }, [rep?.id, token, drillDateFrom, drillDateTo]);

  // Iter 140.13 — kick off the real AI summary in parallel.
  useEffect(() => {
    if (!rep?.id || !token) return;
    let cancelled = false;
    setAiSummary({ loading: true });
    setExpandedRow(null);
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/rep-activity/${rep.id}/ai-summary`,
          // Iter 341 — AI summary must scope to the same window as
          // the drill-down list, otherwise the "highlights say 60
          // calls but the report shows 80" divergence (Iter 159.5)
          // resurfaces the moment Today / Yesterday / Custom is picked.
          drillParams,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 35000 });
        if (!cancelled) setAiSummary({ loading: false, ...res.data });
      } catch (e) {
        if (!cancelled) setAiSummary({ loading: false, error: true, message: e?.response?.data?.detail || "AI summary unavailable" });
      }
    })();
    return () => { cancelled = true; };
  }, [rep?.id, token, drillDateFrom, drillDateTo]);

  const outcomeColor = (o) => {
    if (o === "connected" || o === "demo-booked") return { bg: "#dcfce7", color: "#166534" };
    if (o === "voicemail") return { bg: "#dbeafe", color: "#1e40af" };
    if (o === "no-answer") return { bg: "#f1f5f9", color: "#475569" };
    if (o === "busy") return { bg: "#fef3c7", color: "#854d0e" };
    return { bg: "#f1f5f9", color: "#475569" };
  };
  const fmtSec = (s) => {
    const m = Math.floor((s || 0) / 60), sec = (s || 0) % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };
  // Iter 177.7 (Lisa #ef8f082d Feb 2026) — defensive coercion. Some leads
  // carry a structured `score` object (the AI-scoring engine output:
  // {primary, secondary, campaigns, deal_size, ehr_competitive,
  // talking_point, engine_version, scored_at}) instead of the legacy
  // flat number. Rendering that object directly in JSX throws React
  // error #31 ("Objects are not valid as a React child") — exactly
  // what Lisa hit when clicking "Demos Scheduled" on /reports.
  const numScore = (s) => {
    if (s == null) return 0;
    if (typeof s === "number") return s;
    if (typeof s === "object") {
      // Prefer the legacy `primary` (composite 0-100); fall back to
      // any numeric field; finally to 0 so downstream comparisons +
      // chip colour selection never crash.
      const n = Number(s.primary ?? s.value ?? s.score ?? 0);
      return Number.isFinite(n) ? n : 0;
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="drill-down-modal">
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div style={{ position: "relative", width: rep ? 1080 : 720, maxWidth: "95vw", maxHeight: "88vh", background: "#fff", borderRadius: 16, border: "0.5px solid #d2d2d7", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #f5f5f7", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#1d1d1f" }}>{title}</span>
            {leads.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color, background: color + "15", padding: "2px 8px", borderRadius: 6 }}>{leads.length} leads</span>}
            {rep && callData?.summary && (
              <span style={{ fontSize: 11, fontWeight: 600, color: "#5B6CFF", background: "#5B6CFF15", padding: "2px 8px", borderRadius: 6 }}>
                {callData.summary.events || 0} events · last 30d
              </span>
            )}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="close-drill-down"><X style={{ width: 16, height: 16, color: "#86868b" }} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* Rep Detail View */}
          {rep && (
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: color + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color }}>{rep.avatar}</div>
                <div><div style={{ fontSize: 16, fontWeight: 700 }}>{rep.name}</div><div style={{ fontSize: 12, color: "#888" }}>{rep.role}</div></div>
              </div>
              {/* Live KPI grid — pulls from real backend numbers, not the broken
                  legacy field names (dealsClosed, conversionRate, avgResponseTime). */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
                {[
                  { label: "Calls", value: (rep.calls || 0).toLocaleString() },
                  { label: "Human connect %", value: `${rep.connect_rate || 0}%` },
                  { label: "Emails", value: rep.emails || 0 },
                  { label: "SMS", value: rep.sms || 0 },
                  { label: "Tasks (open / total)", value: `${rep.tasks_open || 0} / ${rep.tasks_total || 0}` },
                  { label: "Contact updates", value: rep.contact_updates || 0 },
                  { label: "Demos Scheduled", value: rep.demos_scheduled || 0 },
                  { label: "Demos Completed", value: rep.demos_completed || 0 },
                  { label: "Closed", value: rep.closed || 0 },
                ].map((m, i) => (
                  <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: i < 4 ? "#111" : color }}>{m.value}</div>
                    <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>{m.label}</div>
                  </div>
                ))}
              </div>
              {/* Outcome-breakdown chip strip */}
              {rep.outcome_breakdown && (
                <div data-testid="rep-outcome-breakdown" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, fontSize: 10 }}>
                  {[
                    { k: "connected", l: "Connected", bg: "#dcfce7", c: "#166534" },
                    { k: "voicemail", l: "Voicemail", bg: "#dbeafe", c: "#1e40af" },
                    { k: "no_answer", l: "No answer", bg: "#f1f5f9", c: "#475569" },
                    { k: "busy", l: "Busy", bg: "#fef3c7", c: "#854d0e" },
                  ].map((o) => (
                    <span key={o.k} style={{ background: o.bg, color: o.c, padding: "3px 9px", borderRadius: 999, fontWeight: 600 }}>
                      {o.l}: <strong style={{ fontVariantNumeric: "tabular-nums" }}>{rep.outcome_breakdown[o.k] || 0}</strong>
                    </span>
                  ))}
                </div>
              )}
              {/* Iter 140.13 — Real AI Summary card (Azure GPT-5 mini).
                  Replaces the legacy hardcoded mock pattern with grounded
                  insights from the rep's actual call activity. */}
              <div data-testid="rep-ai-summary" style={{ border: "0.5px solid #c7d2fe", borderRadius: 10, background: "linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%)", padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <Sparkles style={{ width: 13, height: 13, color: "#5b21b6" }} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#5b21b6", textTransform: "uppercase", letterSpacing: "0.6px" }}>AI summary · last 30d · powered by Azure GPT-5 Mini</div>
                </div>
                {aiSummary?.loading ? (
                  <div style={{ fontSize: 11, color: "#5b21b6", padding: "8px 0" }}>
                    <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
                    Analyzing {rep.name.split(" ")[0]}'s call activity, transcripts, and notes…
                  </div>
                ) : aiSummary?.error ? (
                  <div style={{ fontSize: 11, color: "#9f1239", padding: "8px 0" }}>{aiSummary.message}</div>
                ) : (
                  <div>
                    <div data-testid="rep-ai-headline" style={{ fontSize: 13, fontWeight: 600, color: "#1e1b4b", lineHeight: 1.5, marginBottom: 10 }}>{aiSummary?.headline}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, fontSize: 11 }}>
                      {[
                        { key: "highlights", label: "Highlights", items: aiSummary?.highlights || [], bullet: "✓", color: "#15803d", bg: "#dcfce7" },
                        { key: "risks", label: "Risks", items: aiSummary?.risks || [], bullet: "⚠", color: "#9f1239", bg: "#fee2e2" },
                        { key: "next_actions", label: "Next actions", items: aiSummary?.next_actions || [], bullet: "→", color: "#1e40af", bg: "#dbeafe" },
                      ].map((card) => (
                        <div key={card.key} data-testid={`rep-ai-${card.key}`} style={{ background: "#fff", border: "0.5px solid #e0e7ff", borderRadius: 8, padding: 10 }}>
                          <div style={{ display: "inline-block", background: card.bg, color: card.color, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", padding: "2px 8px", borderRadius: 999, marginBottom: 6 }}>{card.label}</div>
                          {card.items.length === 0 ? (
                            <div style={{ fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>None identified.</div>
                          ) : (
                            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                              {card.items.map((it, i) => (
                                <li key={i} style={{ fontSize: 11, color: "#334155", lineHeight: 1.5, marginBottom: 4, paddingLeft: 14, position: "relative" }}>
                                  <span style={{ position: "absolute", left: 0, color: card.color, fontWeight: 700 }}>{card.bullet}</span>
                                  {it}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Per-call drill-down table (Iter 140.9, expandable Iter 140.13) */}
              <div style={{ border: "0.5px solid #eee", borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ padding: "8px 12px", background: "#fafafa", borderBottom: "0.5px solid #eee", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Call activity · {callData?.window_days ? `last ${callData.window_days}d` : "last 30d"}
                </div>
                {callData?.loading ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#888", fontSize: 11 }}>
                    <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
                    Loading {rep.name}'s calls…
                  </div>
                ) : callData?.error ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#A32D2D", fontSize: 11 }}>{callData.message}</div>
                ) : !callData?.rows || callData.rows.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#aaa", fontStyle: "italic", fontSize: 11 }}>No calls logged in this window.</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: "#FAFBFD", color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>
                        {["", "When", "Lead", "Phone", "Outcome", "Human Connect", "Duration", "Notes / summary"].map((h, hi) => (
                          <th key={hi} style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, borderBottom: "0.5px solid #eee" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {callData.rows.slice(0, 100).map((row, i) => {
                        const oc = outcomeColor(row.outcome);
                        const rowKey = row.id || row.external_id || `r-${i}`;
                        const isExpanded = expandedRow === rowKey;
                        const hasDetail = (row.coach_summary || "") || (row.agent_notes && row.agent_notes.length > 0);
                        return (
                          <React.Fragment key={rowKey}>
                            <tr
                              data-testid={`rep-drill-call-${i}`}
                              style={{ borderBottom: isExpanded ? "0" : "0.5px solid #f5f5f7", cursor: hasDetail ? "pointer" : "default", background: isExpanded ? "#f8fafc" : "transparent" }}
                              onClick={() => { if (hasDetail) setExpandedRow(isExpanded ? null : rowKey); }}
                            >
                              <td style={{ padding: "6px 10px", color: "#94a3b8", width: 18, textAlign: "center" }}>
                                {hasDetail && (isExpanded ? <ChevronDown style={{ width: 10, height: 10 }} /> : <ChevronRight style={{ width: 10, height: 10 }} />)}
                              </td>
                              <td style={{ padding: "6px 10px", color: "#475569", whiteSpace: "nowrap" }}>
                                <div>{row.at ? new Date(row.at).toLocaleString() : "—"}</div>
                                {row.kind === "call" && (row.started_at || row.at) && (() => {
                                  // Feb 2026 (Option A): show wall-clock start → end
                                  // + wall duration under the relative time. When
                                  // `ended_at` isn't stored (no transcript), derive
                                  // it from started_at + duration_sec. Tooltip
                                  // carries the UTC/timezone for audit.
                                  const s = new Date(row.started_at || row.at);
                                  if (isNaN(s.getTime())) return null;
                                  const endSrc = row.ended_at || (row.duration_sec > 0 ? new Date(s.getTime() + row.duration_sec * 1000).toISOString() : null);
                                  const e = endSrc ? new Date(endSrc) : null;
                                  const tfmt = { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true };
                                  const startTxt = s.toLocaleTimeString([], tfmt);
                                  const endTxt = e && !isNaN(e.getTime()) ? e.toLocaleTimeString([], tfmt) : null;
                                  let wallTxt = null;
                                  if (e && !isNaN(e.getTime())) {
                                    const wallSec = Math.max(0, Math.round((e.getTime() - s.getTime()) / 1000));
                                    if (wallSec > 0) {
                                      const mm = Math.floor(wallSec / 60);
                                      const ss = wallSec % 60;
                                      wallTxt = mm > 0 ? `${mm}m ${ss.toString().padStart(2, "0")}s wall` : `${ss}s wall`;
                                    }
                                  }
                                  const title = `Started (local): ${s.toLocaleString()}\nStarted (UTC): ${s.toISOString()}${e ? `\nEnded (local): ${e.toLocaleString()}` : ""}${e && !row.ended_at ? "\n\nEnded time derived from started_at + duration_sec (no transcript on file)." : ""}`;
                                  return (
                                    <div
                                      data-testid={`rep-drill-when-times-${i}`}
                                      title={title}
                                      style={{ fontSize: 9, color: "#94a3b8", lineHeight: 1.35, marginTop: 2, cursor: "help" }}
                                    >
                                      <div style={{ fontVariantNumeric: "tabular-nums" }}>
                                        {startTxt}{endTxt ? ` → ${endTxt}` : ""}
                                      </div>
                                      {wallTxt && (
                                        <div style={{ color: "#64748b", fontSize: 8 }}>{wallTxt}</div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td style={{ padding: "6px 10px", color: "#0f172a" }}>
                                {row.lead_id ? (
                                  <a href={`/lead-focus/${row.lead_id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#5B6CFF", textDecoration: "none", fontWeight: 600 }} data-testid={`rep-drill-lead-link-${i}`}>
                                    {row.lead_name}
                                  </a>
                                ) : (
                                  <span style={{ color: "#94a3b8" }}>{row.lead_name}</span>
                                )}
                                {row.practice && <div style={{ fontSize: 9, color: "#94a3b8" }}>{row.practice}</div>}
                              </td>
                              <td style={{ padding: "6px 10px", color: "#475569", fontFamily: "ui-monospace, monospace" }}>{row.to_number || row.from_number || "—"}</td>
                              <td style={{ padding: "6px 10px" }}>
                                {row.kind === "call" ? (
                                  <span style={{ background: oc.bg, color: oc.color, padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{(row.outcome || "—").replace(/-/g, " ")}</span>
                                ) : (
                                  <span style={{ background: "#ede9fe", color: "#6d28d9", padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>SMS</span>
                                )}
                              </td>
                              <td data-testid={`rep-drill-human-${i}`} style={{ padding: "6px 10px" }}>
                                {row.kind !== "call" ? (
                                  <span style={{ color: "#cbd5e1" }}>—</span>
                                ) : row.human_connect_verdict === true ? (
                                  <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>True</span>
                                ) : row.human_connect_verdict === false ? (
                                  <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>False</span>
                                ) : (
                                  <span style={{ color: "#94a3b8" }} title="Not yet classified">—</span>
                                )}
                              </td>
                              <td style={{ padding: "6px 10px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
                                {row.kind === "call" ? fmtSec(row.duration_sec) : "—"}
                                {/* Feb 2026: audio-file duration chip. Only shown
                                    when the audio probe (Settings → RC Operations
                                    → Deduce Duration from Recording Audio) has
                                    stamped `recording_call_duration` AND it
                                    differs from the CRM-side value by >5s. This
                                    is the case where RC's call-log under-reports
                                    the true conversation length (e.g. call
                                    mss3d7os70bdr9un: RC said 3s but the 2-min
                                    recording has real audio). */}
                                {row.kind === "call" && row.recording_call_duration && Math.abs(row.recording_call_duration - (row.duration_sec || 0)) > 5 && (
                                  <span
                                    data-testid={`rep-drill-audio-dur-${i}`}
                                    title={`CRM duration: ${row.duration_sec || 0}s · True audio length: ${row.recording_call_duration}s\nRC's call-log under-reported the conversation length. The recording is authoritative.`}
                                    style={{
                                      marginLeft: 6, fontSize: 9, fontWeight: 700,
                                      color: "#5856D6", background: "#EEF0FF",
                                      border: "0.5px solid #c7d2fe", borderRadius: 4,
                                      padding: "1px 5px", cursor: "help",
                                      fontVariantNumeric: "tabular-nums",
                                      display: "inline-block", verticalAlign: "middle",
                                    }}
                                  >▶ {fmtSec(row.recording_call_duration)}</span>
                                )}
                              </td>
                              <td style={{ padding: "6px 10px", color: "#475569", maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {row.agent_notes && row.agent_notes.length > 0 ? (
                                  <span style={{ color: "#0f172a", fontWeight: 600 }}>📝 {row.agent_notes[0].text}</span>
                                ) : row.coach_summary || row.text || (row.stage ? <span style={{ color: "#94a3b8" }}>stage: {row.stage}</span> : "—")}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr data-testid={`rep-drill-detail-${i}`} style={{ background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0" }}>
                                <td colSpan={8} style={{ padding: "10px 16px 12px" }}>
                                  {row.agent_notes && row.agent_notes.length > 0 && (
                                    <div style={{ marginBottom: row.coach_summary ? 10 : 0 }}>
                                      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#5B6CFF", marginBottom: 6 }}>
                                        📝 Agent notes ({row.agent_notes.length})
                                      </div>
                                      {row.agent_notes.map((n, ni) => (
                                        <div key={ni} data-testid={`rep-drill-note-${i}-${ni}`} style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", marginBottom: 5, fontSize: 11, color: "#0f172a", lineHeight: 1.5 }}>
                                          <div style={{ whiteSpace: "pre-wrap" }}>{n.text}</div>
                                          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 4 }}>{n.at ? new Date(n.at).toLocaleString() : ""}{n.user_name ? ` · ${n.user_name}` : ""}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {row.coach_summary && (
                                    <div>
                                      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#0891b2", marginBottom: 6 }}>
                                        🎙 Coach summary (auto-generated from transcript)
                                      </div>
                                      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#334155", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                                        {row.coach_summary}
                                      </div>
                                    </div>
                                  )}
                                  {row.lead_id && (
                                    <div style={{ marginTop: 8, fontSize: 10 }}>
                                      <a href={`/lead-focus/${row.lead_id}`} target="_blank" rel="noreferrer" style={{ color: "#5B6CFF", textDecoration: "none", fontWeight: 600 }}>
                                        Open lead in new tab →
                                      </a>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {callData.rows.length > 100 && (
                        <tr><td colSpan={7} style={{ padding: 8, textAlign: "center", color: "#aaa", fontSize: 10, fontStyle: "italic" }}>Showing first 100 of {callData.rows.length} events.</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              {/* Assign Task to Rep */}
              <div style={{ border: "0.5px solid #eee", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", marginBottom: 8 }}>Assign Task to {rep.name.split(" ")[0]}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder="e.g., Follow up with Valley Urgent Care..." className="input" style={{ flex: 1, fontSize: 12 }} data-testid="assign-task-input" />
                  <button onClick={() => { if (taskTitle) { toast.success(`Task assigned to ${rep.name}`); setTaskTitle(""); } }} className="btn btn-primary" style={{ fontSize: 11, padding: "6px 14px" }} data-testid="assign-task-btn">Assign</button>
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                  {["Follow up stale leads", "Schedule demos", "Send proposals", "Update CRM notes"].map(s => (
                    <button key={s} onClick={() => setTaskTitle(s)} style={{ padding: "2px 8px", fontSize: 9, borderRadius: 5, border: "0.5px solid #eee", background: "#fafafa", cursor: "pointer", fontFamily: "var(--font)", color: "#888" }}>{s}</button>
                  ))}
                </div>
              </div>
              <button onClick={() => { toast.success(`Nudge sent to ${rep.name}: Action needed on pipeline leads`); }} style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: "0.5px solid #ff9500", background: "#ff950010", color: "#ff9500", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }} data-testid="nudge-rep-btn">Nudge {rep.name.split(" ")[0]} for Action</button>
            </div>
          )}
          {/* Lead List View */}
          {!rep && leads.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead><tr style={{ borderBottom: "0.5px solid #f5f5f7", position: "sticky", top: 0, background: "#fafafa" }}>
                {["Contact", "Company", "Specialty", "Value", "Score", "Sub-status", "Location"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={i} style={{ borderBottom: "0.5px solid #f5f5f7" }}>
                    <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1d1d1f" }}>{l.contact}</td>
                    <td style={{ padding: "8px 12px", color: "#1d1d1f" }}>{l.company}</td>
                    <td style={{ padding: "8px 12px", color: "#86868b" }}>{l.specialty}</td>
                    <td style={{ padding: "8px 12px", fontWeight: 700 }}>${l.value?.toLocaleString()}</td>
                    <td style={{ padding: "8px 12px" }}>{(() => { const n = numScore(l.score); return <span style={{ fontWeight: 700, color: n >= 71 ? AP.red : n >= 41 ? AP.orange : AP.teal }}>{n}</span>; })()}</td>
                    <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: color + "12", color, fontWeight: 600 }}>{l.sub_status || l.stage}</span></td>
                    <td style={{ padding: "8px 12px", color: "#86868b", fontSize: 10 }}>{l.city && l.state ? `${l.city}, ${l.state}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: "#86868b", fontSize: 12 }}>No leads in this category</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Color Key ─── */
function ColorKey({ items }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "8px 0" }} data-testid="color-key">
      {items.map(item => (
        <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
          <span style={{ fontSize: 10, color: "#86868b", fontWeight: 500 }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Card wrapper ─── */
function Card({ children, title, sub, onExport, canExport: canExp = false, style: s = {} }) {
  return (
    <div style={{ borderRadius: 14, border: "0.5px solid #e5e5ea", background: "#fff", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", ...s }}>
      {(title || (onExport && canExp)) && (
        <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 750, color: "#1d1d1f" }}>{title}</div>
            {sub && <div style={{ fontSize: 10, color: "#86868b" }}>{sub}</div>}
          </div>
          {onExport && canExp && (
            <button onClick={onExport} style={{ background: "none", border: "0.5px solid #d2d2d7", borderRadius: 8, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontFamily: "var(--font)", fontWeight: 600, color: "#86868b" }}>
              <Download style={{ width: 10, height: 10 }} /> CSV
            </button>
          )}
        </div>
      )}
      <div style={{ padding: title ? "0 18px 16px" : "16px 18px" }}>{children}</div>
    </div>
  );
}

/* ─── Funnel row ─── */
function FunnelRow({ label, count, maxCount, color, onClick }) {
  const w = maxCount > 0 ? Math.max((count / maxCount) * 100, 6) : 6;
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, width: "100%", background: "none", border: "none", cursor: onClick ? "pointer" : "default", fontFamily: "var(--font)", padding: "2px 0" }}
      onMouseEnter={e => { if (onClick) e.currentTarget.querySelector(".bar").style.filter = "brightness(1.15)"; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.querySelector(".bar").style.filter = "none"; }}>
      <div style={{ width: 90, fontSize: 10, color: "#86868b", textAlign: "right", flexShrink: 0, fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, height: 26, borderRadius: 6, overflow: "hidden", background: "#f5f5f7" }}>
        <div className="bar" style={{ width: `${w}%`, height: "100%", background: `linear-gradient(135deg, ${color}, ${color}dd)`, borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 8, fontSize: 10, fontWeight: 800, color: "#fff", transition: "all 0.4s ease" }}>{count}</div>
      </div>
    </button>
  );
}

/* ─── Progress bar ─── */
function ProgressBar({ label, pct, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: "#1d1d1f", fontWeight: 500 }}>{label}</span>
        <span style={{ fontWeight: 700, color }}>{pct}%</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: "#f5f5f7" }}>
        <div style={{ height: "100%", borderRadius: 3, background: `linear-gradient(90deg, ${color}, ${color}cc)`, width: `${pct}%`, transition: "width 0.5s ease" }} />
      </div>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "pipeline", label: "Pipeline" },
  { id: "demo", label: "Demo & Engagement" },
  { id: "performance", label: "Rep Performance" },
  { id: "comms", label: "Comms Dashboard" },
  { id: "rc_connections", label: "RC Connections", adminOnly: true },
  // Iter 179.12 (Lisa #c4ec2214 Feb 2026) — reps now see "Rep Activity" too,
  // server-side scoped to their own data. The AdminRepActivity component
  // hides admin-only panels (compliance, LLM cost, coaching) when the
  // viewer is not a manager.
  { id: "rep_activity", label: "Rep Activity" },
  { id: "training", label: "Training", adminOnly: true },
];

const REPORT_TYPES = [
  { id: "pipeline", label: "Pipeline Report", desc: "All leads by stage, value, source", icon: Target, color: AP.blue },
  { id: "quotes", label: "Quotes Report", desc: "All quotes with status and values", icon: FileText, color: AP.indigo },
  { id: "activity", label: "Activity Report", desc: "Recent lead activities and events", icon: Activity, color: AP.green },
];

/* New Leads Card with source filtering */
function NewLeadsCard({ token }) {
  const [sourceData, setSourceData] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- token read at call-time; run once per mount
  useEffect(() => { axios.get(`${API}/reports/lead-sources`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setSourceData(r.data)).catch(() => {}); }, []);
  if (!sourceData) return null;
  const all = sourceData.breakdown.flatMap(b => b.leads.map(l => ({ ...l, lead_source: b.source })));
  const filtered = all.filter(l => {
    if (sourceFilter !== "all" && l.lead_source !== sourceFilter) return false;
    if (search) { const s = search.toLowerCase(); return (l.contact_name || "").toLowerCase().includes(s) || (l.title || "").toLowerCase().includes(s); }
    return true;
  });
  return (
    <Card title={`New Leads by Source (${sourceData.total_leads} total)`}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => setSourceFilter("all")} style={{ padding: "3px 10px", borderRadius: 6, fontSize: 10, background: sourceFilter === "all" ? "#1b365D" : "#f5f5f5", color: sourceFilter === "all" ? "#fff" : "#888", border: "none", cursor: "pointer", fontFamily: "var(--font)", fontWeight: 600 }}>All ({sourceData.total_leads})</button>
        {sourceData.breakdown.map(b => (
          <button key={b.source} onClick={() => setSourceFilter(b.source)} style={{ padding: "3px 10px", borderRadius: 6, fontSize: 10, background: sourceFilter === b.source ? "#1b365D" : "#f5f5f5", color: sourceFilter === b.source ? "#fff" : "#888", border: "none", cursor: "pointer", fontFamily: "var(--font)", fontWeight: 500 }} data-testid={`new-lead-filter-${b.source}`}>{b.source} ({b.count})</button>
        ))}
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." style={{ width: "100%", padding: "6px 10px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 11, marginBottom: 8, fontFamily: "var(--font)" }} data-testid="new-leads-search" />
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {filtered.slice(0, 20).map(l => (
          <div key={l.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "0.5px solid #f5f5f5", fontSize: 11 }}>
            <div><span style={{ fontWeight: 600 }}>{l.contact_name || "—"}</span> <span style={{ color: "#888" }}>— {l.title || ""}</span></div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={{ color: "#888" }}>{l.stage}</span>
              <span style={{ fontWeight: 600 }}>${(l.value || 0).toLocaleString()}</span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ fontSize: 11, color: "#888", padding: 10, textAlign: "center" }}>No leads found</div>}
      </div>
    </Card>
  );
}

/* Rep Performance data is now fetched live from /api/reports/rep-performance.
   Shape per row: { id, name, email, role, avatar, calls, emails, demos,
   proposals, closed, revenue, pipeline, active_leads, connect_rate,
   demo_rate, close_rate }. Empty array when no real reps have activity. */

export default function Reports() {
  const { token, user } = useAuth();
  const userCanExport = canExport(user?.role);
  // Honor ?tab=… (e.g. /reports?tab=comms from /comms-dashboard redirect)
  const initialTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "overview";
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState(0);
  const [activeRange, setActiveRange] = useState({ from: null, to: null });
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [drillDown, setDrillDown] = useState(null);
  const [exportLoading, setExportLoading] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [customReport, setCustomReport] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [reps, setReps] = useState([]);
  const [repsLoading, setRepsLoading] = useState(false);
  // Multi-select agent filter for the Rep Performance tab (Jun 2026).
  // Empty => all reps. Filters the already-fetched `reps` list client-side
  // so both the KPI cards and the table reflect the selection.
  const [perfAgentIds, setPerfAgentIds] = useState([]);
  // Aug 2026 — Meeting Drill Modal state. Populated when the user clicks
  // a Demos Scheduled / Demos Completed cell on the Rep Performance table.
  const [demoDrill, setDemoDrill] = useState(null);       // { rep, bucket, rows[], loading }

  // Ask the backend for the exact demos that fed a rep's cell and open
  // the modal. Uses the currently-applied date window so the list
  // agrees with the number the manager clicked on.
  const openDemoDrill = async (rep, bucket) => {
    setDemoDrill({ rep, bucket, rows: [], loading: true });
    try {
      const params = new URLSearchParams({ rep_id: rep.id, bucket });
      if (customFrom) params.set("date_from", customFrom);
      if (customTo)   params.set("date_to", customTo);
      const res = await axios.get(`${API}/reports/rep-demos-drill?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDemoDrill({ rep, bucket, rows: res.data?.rows || [], loading: false });
    } catch (e) {
      toast.error("Failed to load demo details");
      setDemoDrill(null);
    }
  };

  const fetchData = async (from, to, { force = false } = {}) => {
    setLoading(true);
    setActiveRange({ from: from || null, to: to || null });
    try {
      let url = `${API}/reports/analytics`;
      const params = [];
      if (from) params.push(`date_from=${from}`);
      if (to) params.push(`date_to=${to}`);
      // `?nocache=1` bypasses the backend's 60-second analytics cache
      // (see routes/reports_routes.py::ANALYTICS_CACHE_TTL_SECONDS).
      // Used by the Refresh button to guarantee fresh numbers.
      if (force) params.push("nocache=1");
      if (params.length) url += "?" + params.join("&");
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch { toast.error("Failed to load analytics"); }
    setLoading(false);
  };

  const fetchReps = async (from, to) => {
    if (!token) return;
    setRepsLoading(true);
    try {
      let url = `${API}/reports/rep-performance`;
      const params = [];
      if (from) params.push(`date_from=${from}`);
      if (to) params.push(`date_to=${to}`);
      if (params.length) url += "?" + params.join("&");
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setReps(Array.isArray(res.data?.reps) ? res.data.reps : []);
    } catch { /* silent — page falls back to empty-state */ }
    setRepsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData defined above; intentional key on token change
  useEffect(() => { fetchData(null, null); fetchReps(null, null); }, [token]);

  const applyDatePreset = (idx) => {
    setDatePreset(idx);
    const { from, to } = getDateRange(DATE_PRESETS[idx]);
    fetchData(from, to);
    fetchReps(from, to);
  };

  const applyCustomDate = () => {
    if (customFrom && customTo) { setDatePreset(-1); fetchData(customFrom, customTo); fetchReps(customFrom, customTo); }
  };

  // Rep Performance multi-select (Jun 2026) — manager gate + client-side
  // narrowing of the fetched reps for both KPIs and the table.
  const perfIsManager = ["super_admin", "crm_admin", "sales_manager", "marketing_manager"].includes(user?.role);
  const visibleReps = perfAgentIds.length ? reps.filter((r) => perfAgentIds.includes(r.id)) : reps;
  const perfAgentOptions = reps.map((r) => ({ id: r.id, name: r.name, role: r.role }));

  // Iter 177.7 (Lisa #ef8f082d Feb 2026) — normalise the `score` field
  // on every lead BEFORE handing it to the drill-down modal. Some
  // backend payloads now carry a structured `ai_score` object instead
  // of a flat 0-100 number; rendering that object in JSX (Reports.jsx
  // line 478, 1042) throws React error #31 and the modal crashes.
  // Coercing here means every downstream `l.score` comparison +
  // render gets a number, no other site has to know.
  const _normaliseScore = (l) => {
    const s = l?.score;
    if (s == null || typeof s === "number") return l;
    if (typeof s === "object") {
      const n = Number(s.primary ?? s.value ?? s.score ?? 0);
      return { ...l, score: Number.isFinite(n) ? n : 0 };
    }
    const n = Number(s);
    return Number.isFinite(n) ? { ...l, score: n } : { ...l, score: 0 };
  };

  /* Stage drill-down: get leads for a specific pipeline stage */
  const drillStage = (stageObj) => {
    const allLeads = Object.values(data?.stage_leads || {}).flat().map(_normaliseScore);
    const matching = allLeads.filter(l => stageObj.dbStages.includes(l.stage));
    setDrillDown({ title: stageObj.label, leads: matching, color: stageObj.color });
  };

  /* Generic drill-down from KPI */
  const drillKPI = (title, filterFn, color) => {
    const allLeads = Object.values(data?.stage_leads || {}).flat().map(_normaliseScore);
    setDrillDown({ title, leads: allLeads.filter(filterFn), color });
  };

  const exportReport = async (type, format) => {
    setExportLoading(`${type}-${format}`);
    try {
      const res = await axios.post(`${API}/reports/export`, { type }, { headers: { Authorization: `Bearer ${token}` } });
      const { rows, title } = res.data;
      if (format === "csv") downloadCSV(rows, `${title.replace(/\s/g, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}`);
      else printReport(title, rows);
    } catch { toast.error("Export failed"); }
    finally { setExportLoading(null); }
  };

  const exportTabCSV = () => {
    if (!data) return;
    let rows = [], name = "report";
    const o = data?.overview || {};
    const p = data?.pipeline || {};
    const d = data?.demo || {};
    const pf = data?.performance || {};
    if (tab === "overview") {
      name = "overview_report";
      rows = [
        { Metric: "Total Leads", Value: o.total_leads }, { Metric: "Closed Won", Value: o.closed_won },
        { Metric: "Conversion Rate", Value: `${o.conversion_rate}%` }, { Metric: "Pipeline Value", Value: `$${o.pipeline_value?.toLocaleString()}` },
        ...o.source_breakdown?.map(s => ({ Metric: `Source: ${s.name}`, Value: s.value, Pct: `${s.pct}%` })) || [],
        ...o.product_data?.map(pp => ({ Metric: `Product: ${pp.name}`, Value: pp.value, Pct: `${pp.pct}%` })) || [],
      ];
    } else if (tab === "pipeline") {
      name = "pipeline_report";
      rows = [...(p.hot_table || []).map(h => ({ Contact: h.contact, Company: h.company, Stage: h.stage, Score: h.score, Value: `$${h.value?.toLocaleString()}`, Next_Action: h.next_action }))];
      if (!rows.length) rows = PIPELINE_STAGES.map(s => ({ Stage: s.label, Count: (data?.stage_leads?.[s.id] || []).length }));
    } else if (tab === "demo") {
      name = "demo_engagement_report";
      rows = [
        { Metric: "Demo Show Rate", Value: `${d.show_rate}%` }, { Metric: "No-Shows", Value: d.no_shows },
        ...d.engagement_data?.map(e => ({ Metric: `Engagement: ${e.name}`, Value: e.value })) || [],
        ...d.pain_data?.map(pp => ({ Metric: `Pain Point: ${pp.name}`, Value: pp.value })) || [],
      ];
    } else {
      name = "rep_performance_report";
      rows = [
        ...pf.specialty_data?.map(s => ({ Specialty: s.name, Leads: s.value })) || [],
        ...pf.impl_tracker?.map(t => ({ Practice: t.practice, Product: t.product, Stage: t.stage, Status: t.status })) || [],
      ];
    }
    downloadCSV(rows, `${name}_${new Date().toISOString().split("T")[0]}`);
  };

  const generateCustomReport = async () => {
    if (!customPrompt.trim()) return;
    setCustomLoading(true);
    try {
      const res = await axios.post(`${API}/ai/custom-report`, { prompt: customPrompt }, { headers: { Authorization: `Bearer ${token}` } });
      setCustomReport(res.data);
      // Iter 177.9 (Lisa #7438bfc7 Feb 2026) — "report says generated
      // but where do I view it?" The report DOES render in the
      // `#custom-report-output` block below the prompt input, but
      // the input lives near the top of /reports and the result
      // anchor is several hundred px lower on the page, so on a
      // typical viewport reps never scrolled into view. Toast now
      // has an explicit "View" action button that scrolls the
      // result into focus, AND we auto-scroll on success so reps
      // see their output even if they ignore the toast.
      setTimeout(() => {
        const el = document.getElementById("custom-report-output");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      toast.success("Custom report generated", {
        action: {
          label: "View",
          onClick: () => {
            const el = document.getElementById("custom-report-output");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          },
        },
        duration: 8000,
      });
    } catch { toast.error("Report generation failed"); }
    finally { setCustomLoading(false); }
  };

  if (loading && !data) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}><Loader2 style={{ width: 24, height: 24, animation: "spin 0.8s linear infinite", color: AP.blue }} /></div>;

  const o = data?.overview || {};
  const p = data?.pipeline || {};
  const d = data?.demo || {};
  const pf = data?.performance || {};
  const sl = data?.stage_leads || {};

  /* Compute pipeline counts synced with Leads.jsx stages */
  const pipelineCounts = PIPELINE_STAGES.map(s => ({
    ...s,
    count: s.dbStages.reduce((sum, db) => sum + (sl[db]?.length || 0), 0),
    value: s.dbStages.reduce((sum, db) => sum + (sl[db] || []).reduce((v, l) => v + (l.value || 0), 0), 0),
  }));
  const maxFunnel = Math.max(...pipelineCounts.map(s => s.count), 1);

  return (
    <div data-testid="reports-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">EHI Lead Tracker &mdash; Real-time analytics from your pipeline</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {userCanExport && tab !== "rep_activity" && (
            <button className="btn" onClick={exportTabCSV} data-testid="export-tab-csv" style={{ fontSize: 11 }}>
              <FileSpreadsheet style={{ width: 12, height: 12 }} /> Export CSV
            </button>
          )}
          <button className="btn" onClick={() => window.print()} style={{ fontSize: 11 }}>
            <Printer style={{ width: 12, height: 12 }} /> Print
          </button>
        </div>
      </header>

      <div className="page-content" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Date Range Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="date-range-filter">
          <CalendarDays style={{ width: 14, height: 14, color: "#86868b" }} />
          <span style={{ fontSize: 11, color: "#86868b", fontWeight: 600 }}>Date Range:</span>
          {DATE_PRESETS.map((pr, i) => (
            <button key={i} onClick={() => applyDatePreset(i)} data-testid={`date-preset-${i}`}
              style={{
                padding: "5px 12px", fontSize: 11, borderRadius: 8, fontWeight: datePreset === i ? 650 : 400,
                border: datePreset === i ? `1px solid ${AP.blue}` : "0.5px solid #d2d2d7",
                background: datePreset === i ? AP.blue + "12" : "#fff",
                color: datePreset === i ? AP.blue : "#86868b",
                cursor: "pointer", fontFamily: "var(--font)", transition: "all 0.15s",
              }}>
              {pr.label}
            </button>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8 }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 6, border: "0.5px solid #d2d2d7", fontFamily: "var(--font)" }} data-testid="date-from" />
            <span style={{ fontSize: 10, color: "#86868b" }}>to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 6, border: "0.5px solid #d2d2d7", fontFamily: "var(--font)" }} data-testid="date-to" />
            <button onClick={applyCustomDate} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 6, border: "0.5px solid " + AP.blue, background: AP.blue + "12", color: AP.blue, cursor: "pointer", fontFamily: "var(--font)", fontWeight: 600 }} data-testid="apply-custom-date">Apply</button>
          </div>
          {loading && <Loader2 style={{ width: 14, height: 14, animation: "spin 0.8s linear infinite", color: AP.blue, marginLeft: 4 }} />}
          {/* Refresh — force a fresh analytics computation (bypasses the
              60s backend cache). The button is always available; the
              "Cached" pill is shown only when the latest response was
              served from cache so users know what they're refreshing. */}
          <button
            onClick={() => {
              if (datePreset >= 0) applyDatePreset(datePreset);
              else if (customFrom && customTo) fetchData(customFrom, customTo, { force: true });
              else fetchData(null, null, { force: true });
            }}
            disabled={loading}
            data-testid="reports-refresh-btn"
            title={data?._cache?.hit ? "Serving cached data — click to refresh" : "Refresh"}
            style={{
              marginLeft: 6, fontSize: 10, padding: "4px 10px", borderRadius: 6,
              border: "0.5px solid #d2d2d7", background: "#fff", color: "#1d1d1f",
              cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1,
              fontFamily: "var(--font)", fontWeight: 600, display: "inline-flex",
              alignItems: "center", gap: 4,
            }}
          >
            <RefreshCw style={{ width: 11, height: 11 }} /> Refresh
          </button>
          {data?._cache?.hit && !loading && (
            <span
              data-testid="reports-cache-indicator"
              title={`Cached ${data._cache.ttl_seconds}s — click Refresh for live numbers`}
              style={{
                fontSize: 9.5, padding: "2px 6px", borderRadius: 4, fontWeight: 600,
                background: "#fef3c7", color: "#92400e", letterSpacing: 0.2,
              }}
            >
              CACHED
            </span>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 3, background: "#f5f5f7", borderRadius: 12, padding: 3, width: "fit-content" }}>
          {TABS.filter(t => !t.adminOnly || ["super_admin", "crm_admin"].includes(user?.role)).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`report-tab-${t.id}`}
              style={{
                padding: "8px 18px", fontSize: 12, fontWeight: tab === t.id ? 650 : 400, borderRadius: 9,
                background: tab === t.id ? "#fff" : "transparent",
                color: tab === t.id ? "#1d1d1f" : "#86868b",
                border: "none", cursor: "pointer", fontFamily: "var(--font)", transition: "all 0.15s",
                boxShadow: tab === t.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {tab === "overview" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <KPI label="Total Leads" value={o.total_leads || 0} sub={`$${(o.pipeline_value || 0).toLocaleString()} pipeline`} subColor={AP.blue} icon={Users} color={AP.blue} onClick={() => drillKPI("All Leads", () => true, AP.blue)} />
              <KPI label="Closed Won" value={o.closed_won || 0} icon={TrendingUp} color={AP.green} onClick={() => drillKPI("Closed Won", l => ["won", "closed_won", "contract"].includes(l.stage), AP.green)} />
              <KPI label="Conversion Rate" value={`${o.conversion_rate || 0}%`} sub="Demo to Close" icon={Target} color={AP.orange} />
              <KPI label="Avg Deal Size" value={`$${(o.avg_deal || 0).toLocaleString()}`} icon={DollarSign} color={AP.indigo} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <KPI label="Demos Scheduled" value={o.demos_scheduled || 0} icon={Calendar} color={AP.teal} onClick={() => drillKPI("Demos Scheduled", l => ["demo_scheduled", "demo_confirmed", "demo_completed", "proposal", "quote_sent", "decision", "negotiation", "won", "closed_won", "contract", "implementation"].includes(l.stage) || (l.stage === "demo"), AP.teal)} />
              <KPI label="Demos Completed" value={o.demos_completed || 0} sub={`${o.demos_scheduled > 0 ? Math.round(o.demos_completed / o.demos_scheduled * 100) : 0}% show rate`} subColor={AP.green} icon={Activity} color={AP.green} onClick={() => drillKPI("Demos Completed", l => ["demo_completed", "proposal", "quote_sent", "decision", "negotiation", "won", "closed_won", "contract", "implementation"].includes(l.stage) || (l.stage === "demo" && DEMO_COMPLETED_SUB_STATUSES.includes(l.sub_status)), AP.green)} />
              <KPI label="Proposals Sent" value={o.proposals_sent || 0} icon={FileText} color={AP.purple} onClick={() => drillKPI("Proposals Sent", l => ["proposal", "quote_sent", "decision", "negotiation", "won", "closed_won", "contract"].includes(l.stage), AP.purple)} />
              <KPI label="Hot Leads" value={o.hot_leads || 0} sub="Score 71+" subColor={AP.red} icon={Zap} color={AP.red} onClick={() => drillKPI("Hot Leads (Score 71+)", l => l.score >= 71 && !["lost", "closed_lost", "inactive", "stalled"].includes(l.stage), AP.red)} />
            </div>
            <ClientDemoActivity from={activeRange.from} to={activeRange.to} Card={Card} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card title="Monthly Leads vs Closed Won">
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={o.monthly_data || []}>
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#86868b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#86868b" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CTooltip />} />
                    <Bar dataKey="leads" fill={AP.blue} name="Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="won" fill={AP.green} name="Won" radius={[4, 4, 0, 0]} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title="Lead Source Breakdown">
                <ResponsiveContainer width="100%" height={210}>
                  <RechartPie>
                    <Pie data={o.source_breakdown || []} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 0.5 }} stroke="none">
                      {(o.source_breakdown || []).map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<CTooltip />} />
                  </RechartPie>
                </ResponsiveContainer>
              </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <Card title="Final Lead Status">
                <ColorKey items={[{ label: "Won", color: AP.green }, { label: "Open", color: AP.blue }, { label: "Lost", color: AP.red }]} />
                <ResponsiveContainer width="100%" height={170}>
                  <RechartPie>
                    <Pie data={o.status_breakdown || []} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} stroke="none">
                      {(o.status_breakdown || []).map((e, i) => <Cell key={i} fill={[AP.green, AP.blue, AP.red][i]} />)}
                    </Pie>
                    <Tooltip content={<CTooltip />} />
                  </RechartPie>
                </ResponsiveContainer>
              </Card>
              <Card title="Lead Rating Distribution">
                <ColorKey items={Object.entries(RATING_MAP).map(([label, color]) => ({ label, color }))} />
                {(o.rating_data || []).map((r) => <FunnelRow key={r.name} label={r.name} count={r.value} maxCount={Math.max(...(o.rating_data || []).map(x => x.value), 1)} color={RATING_MAP[r.name]} onClick={() => drillKPI(`Rating: ${r.name}`, l => { const s = l.score || 0; if (r.name === "Hot") return s >= 71; if (r.name === "Warm") return s >= 41 && s < 71; if (r.name === "Cold") return s > 0 && s < 41; return s === 0; }, RATING_MAP[r.name])} />)}
              </Card>
              <Card title="Top Product Interest">
                {(o.product_data || []).slice(0, 6).map((pr, i) => <ProgressBar key={pr.name} label={pr.name} pct={pr.pct} color={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
              </Card>
            </div>

            {/* Lead Source Breakdown — Count per source */}
            <Card title="Leads by Source — Count Breakdown">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, marginBottom: 10 }}>
                {(o.source_breakdown || []).map((s, i) => (
                  <div key={s.name} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "10px 12px", textAlign: "center", cursor: "pointer" }}
                    onClick={() => drillKPI(`Source: ${s.name}`, l => (l.lead_source || "").toLowerCase().includes(s.name.toLowerCase()), PIE_PALETTE[i % PIE_PALETTE.length])} data-testid={`source-card-${s.name}`}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: PIE_PALETTE[i % PIE_PALETTE.length] }}>{s.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 500, color: "#888", marginTop: 2 }}>{s.name}</div>
                    <div style={{ fontSize: 9, color: "#aaa" }}>{s.pct}% of total</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* New Leads — Filterable Card */}
            <NewLeadsCard token={token} drillKPI={drillKPI} />
          </>
        )}

        {/* ═══ PIPELINE (synced with Leads stages) ═══ */}
        {tab === "pipeline" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {pipelineCounts.slice(0, 4).map(s => (
                <KPI key={s.id} label={s.label} value={s.count} sub={`$${s.value.toLocaleString()}`} subColor={s.color} icon={[Users, Activity, Target, FileText][PIPELINE_STAGES.indexOf(PIPELINE_STAGES.find(x => x.id === s.id)) % 4]} color={s.color} onClick={() => drillStage(s)} />
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {pipelineCounts.slice(4).map(s => (
                <KPI key={s.id} label={s.label} value={s.count} sub={`$${s.value.toLocaleString()}`} subColor={s.color} icon={[Target, TrendingUp, Activity, Clock][PIPELINE_STAGES.indexOf(PIPELINE_STAGES.find(x => x.id === s.id)) % 4]} color={s.color} onClick={() => drillStage(s)} />
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card title="Pipeline Funnel by Stage" sub="Click a stage to see leads">
                <ColorKey items={pipelineCounts.map(s => ({ label: s.label, color: s.color }))} />
                {pipelineCounts.map(s => <FunnelRow key={s.id} label={s.label} count={s.count} maxCount={maxFunnel} color={s.color} onClick={() => drillStage(s)} />)}
              </Card>
              <Card title="Budget Range Distribution">
                {(p.budget_data || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={p.budget_data} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 9, fill: "#86868b" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#86868b" }} width={90} axisLine={false} tickLine={false} />
                      <Tooltip content={<CTooltip />} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                        {(p.budget_data || []).map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>No budget data</div>}
              </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <Card title="Decision Timeline">
                {(p.timeline_data || []).map((t, i) => <ProgressBar key={t.name} label={t.name} pct={t.pct} color={[AP.red, AP.orange, AP.yellow, AP.teal, AP.blue, AP.gray][i % 6]} />)}
              </Card>
              <Card title="Pricing Fit">
                {(p.pricing_data || []).length > 0 ? (
                  <>
                    <ColorKey items={(p.pricing_data || []).map((pr, i) => ({ label: pr.name, color: [AP.green, AP.orange, AP.red, AP.gray][i % 4] }))} />
                    <ResponsiveContainer width="100%" height={160}>
                      <RechartPie>
                        <Pie data={p.pricing_data} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} stroke="none">
                          {(p.pricing_data || []).map((_, i) => <Cell key={i} fill={[AP.green, AP.orange, AP.red, AP.gray][i % 4]} />)}
                        </Pie>
                        <Tooltip content={<CTooltip />} />
                      </RechartPie>
                    </ResponsiveContainer>
                  </>
                ) : <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>No data</div>}
              </Card>
              <Card title="Lead Rating">
                <ColorKey items={Object.entries(RATING_MAP).map(([label, color]) => ({ label, color }))} />
                {(o.rating_data || []).map(r => <FunnelRow key={r.name} label={r.name} count={r.value} maxCount={Math.max(...(o.rating_data || []).map(x => x.value), 1)} color={RATING_MAP[r.name]} onClick={() => drillKPI(`Rating: ${r.name}`, l => { const s = l.score || 0; if (r.name === "Hot") return s >= 71; if (r.name === "Warm") return s >= 41 && s < 71; if (r.name === "Cold") return s > 0 && s < 41; return s === 0; }, RATING_MAP[r.name])} />)}
              </Card>
            </div>
            {(p.hot_table || []).length > 0 && (
              <Card title="Hot Leads — Action Required" onExport={() => downloadCSV(p.hot_table.map(h => ({ Contact: h.contact, Company: h.company, Specialty: h.specialty, Stage: h.stage, Score: h.score, Value: `$${h.value?.toLocaleString()}`, Next_Action: h.next_action, Stale_Days: h.days_stale })), "hot_leads_action")} canExport={userCanExport} style={{ padding: 0 }}>
                <div style={{ overflowX: "auto", padding: "0 0 4px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead><tr style={{ borderBottom: "0.5px solid #f5f5f7" }}>
                      {["Contact", "Company", "Specialty", "Stage", "Score", "Value", "Next Action", "Stale"].map(h => (
                        <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{(p.hot_table || []).map((r, i) => (
                      <tr key={i} style={{ borderBottom: "0.5px solid #f5f5f7" }}>
                        <td style={{ padding: "8px 14px", fontWeight: 600, color: "#1d1d1f" }}>{r.contact}</td>
                        <td style={{ padding: "8px 14px" }}>{r.company}</td>
                        <td style={{ padding: "8px 14px", color: "#86868b" }}>{r.specialty}</td>
                        <td style={{ padding: "8px 14px" }}><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, background: AP.blue + "12", color: AP.blue, fontWeight: 600 }}>{r.stage?.replace(/_/g, " ")}</span></td>
                        <td style={{ padding: "8px 14px", fontWeight: 800, color: r.score >= 71 ? AP.red : r.score >= 41 ? AP.orange : AP.teal }}>{r.score}</td>
                        <td style={{ padding: "8px 14px", fontWeight: 700 }}>${r.value?.toLocaleString()}</td>
                        <td style={{ padding: "8px 14px", fontSize: 10, color: "#86868b" }}>{r.next_action}</td>
                        <td style={{ padding: "8px 14px", fontWeight: 700, color: r.days_stale > 7 ? AP.red : r.days_stale > 3 ? AP.orange : AP.green }}>{r.days_stale}d</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* ═══ DEMO & ENGAGEMENT ═══ */}
        {tab === "demo" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <KPI label="Demo Show Rate" value={`${d.show_rate || 0}%`} sub={d.show_rate < 80 ? "Below target" : "On target"} subColor={d.show_rate < 80 ? AP.orange : AP.green} icon={Activity} color={AP.blue} />
              <KPI label="No-Shows" value={d.no_shows || 0} icon={AlertTriangle} color={AP.red} onClick={() => drillKPI("No-Shows (3+ reschedules)", l => ["demo_scheduled", "demo_confirmed"].includes(l.stage), AP.red)} />
              <KPI label="Avg Reschedules" value={d.avg_reschedules || 0} sub="Per lead" icon={Clock} color={AP.orange} />
              <KPI label="Post-Demo Win" value={`${d.post_demo_win || 0}%`} sub={d.post_demo_win >= 25 ? "Strong" : "Needs improvement"} subColor={d.post_demo_win >= 25 ? AP.green : AP.orange} icon={TrendingUp} color={AP.green} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card title="Engagement Level Distribution">
                <ColorKey items={(d.engagement_data || []).map((e, i) => ({ label: e.name, color: [AP.green, AP.blue, AP.orange, AP.red, AP.gray][i % 5] }))} />
                {(d.engagement_data || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={d.engagement_data}>
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#86868b" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9, fill: "#86868b" }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>{(d.engagement_data || []).map((_, i) => <Cell key={i} fill={[AP.green, AP.blue, AP.orange, AP.red, AP.gray][i % 5]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>No data</div>}
              </Card>
              <Card title="Pain Point Categories">
                {(d.pain_data || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={d.pain_data} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 9, fill: "#86868b" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 8, fill: "#86868b" }} width={140} axisLine={false} tickLine={false} />
                      <Tooltip content={<CTooltip />} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>{(d.pain_data || []).map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>No data</div>}
              </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <Card title="Top Feature Interests">
                {(d.product_data || []).slice(0, 5).map((pr, i) => <ProgressBar key={pr.name} label={pr.name} pct={pr.pct} color={PIE_PALETTE[i % PIE_PALETTE.length]} />)}
              </Card>
              <Card title="Product Value Drivers">
                {(d.product_data || []).slice(0, 5).map((pr, i) => (
                  <div key={pr.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, fontSize: 11, color: "#1d1d1f", fontWeight: 500 }}>{pr.name}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: PIE_PALETTE[i % PIE_PALETTE.length] }}>{pr.value} leads</span>
                  </div>
                ))}
              </Card>
              <Card title="Lead Rating">
                <ColorKey items={Object.entries(RATING_MAP).map(([label, color]) => ({ label, color }))} />
                <ResponsiveContainer width="100%" height={160}>
                  <RechartPie>
                    <Pie data={o.rating_data || []} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={55} stroke="none">
                      {(o.rating_data || []).map((e) => <Cell key={e.name} fill={RATING_MAP[e.name]} />)}
                    </Pie>
                    <Tooltip content={<CTooltip />} />
                  </RechartPie>
                </ResponsiveContainer>
              </Card>
            </div>
          </>
        )}

        {/* ═══ REP PERFORMANCE ═══ */}
        {tab === "performance" && (
          <>
            <RcAttributionBanner hoursBack={24} />

            {/* Data-quality nudge — surfaces when a meaningful fraction of
                open leads have no `value` set. Without this banner the
                "Pipeline" column reads as "max=$99,999 cap"; with it the
                user understands *why* the number is small. Threshold:
                show when ≥10% of active leads across all reps are
                unvalued. Computed inline so the banner stays in sync
                with whatever date filter is active. */}
            {(() => {
              const totalActive = reps.reduce((s, r) => s + (r.active_leads || 0), 0);
              const totalUnvalued = reps.reduce((s, r) => s + (r.leads_without_value || 0), 0);
              if (!totalActive || totalUnvalued / totalActive < 0.1) return null;
              const pct = Math.round((totalUnvalued / totalActive) * 100);
              return (
                <div data-testid="rep-perf-data-quality-banner"
                     style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                              border: "0.5px solid " + AP.orange, background: AP.orange + "10",
                              borderRadius: 8, fontSize: 11, color: "#1d1d1f", marginBottom: 8 }}>
                  <AlertTriangle style={{ width: 14, height: 14, color: AP.orange, flexShrink: 0 }} />
                  <span>
                    <strong>{pct}% of open leads have no <code>value</code> set</strong>
                    {" "}({totalUnvalued.toLocaleString()} of {totalActive.toLocaleString()}). The Pipeline column sums the <code>value</code> field — when most leads are unvalued, the dollar figure is largely meaningless. Fix it by setting <code>value</code> on each lead, or backfill via Settings → Data → Lead value defaults.
                  </span>
                </div>
              );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <KPI label="Avg Days to Close" value={pf.avg_days_close || 0} sub={pf.avg_days_close <= 21 ? "On target" : "Above target"} subColor={pf.avg_days_close <= 21 ? AP.green : AP.orange} icon={Clock} color={AP.blue} />
              <KPI label="Re-engagement Rate" value={`${pf.reeng_rate || 0}%`} sub="10+ day dormant" icon={Activity} color={AP.orange} onClick={() => drillKPI("Re-engagement Needed", l => !["won", "closed_won", "contract", "implementation"].includes(l.stage), AP.orange)} />
              <KPI label="Total Revenue" value={`$${visibleReps.reduce((s, r) => s + (r.revenue || 0), 0).toLocaleString()}`} icon={DollarSign} color={AP.green} />
              <KPI label="Avg Close Rate" value={`${visibleReps.length ? Math.round(visibleReps.reduce((s, r) => s + (r.close_rate || 0), 0) / visibleReps.length) : 0}%`} icon={TrendingUp} color={AP.indigo} />
            </div>

            {/* Rep Performance Table — column meanings (Feb 2026 super-user audit):
                  Calls   — DISTINCT call events (deduped by call_id when present).
                            Title-tooltip shows the dial-attempts breakdown.
                  Demos Scheduled  — meetings booked (organizer_user_id = rep) in
                            window (created_at anchor). Excludes cancels. No-shows
                            still count — the rep did the booking work.
                  Demos Completed  — meetings whose status flipped to "completed"
                            in window. Source: db.meetings.status_history. Sub-
                            line shows show-rate = completed / scheduled.
                  Pipeline — sum of `value` field on active leads. The
                            cell now shows "N unvalued" beneath the dollar
                            figure when there are leads w/o a value set.
                  Open Leads — renamed from "Active Leads" to clarify
                            ownership semantics (count of leads where
                            this rep is owner_id). */}
            {perfIsManager && (
              <div data-testid="rep-perf-filter-bar" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "#86868b" }}>Filter agents:</span>
                <AgentMultiSelect
                  options={perfAgentOptions}
                  selected={perfAgentIds}
                  onChange={setPerfAgentIds}
                  testidPrefix="reports-perf-agent"
                />
              </div>
            )}
            <Card title="Sales Agent Performance — All Reps">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead><tr style={{ borderBottom: "0.5px solid #eee" }}>
                    {[
                      { h: "Rep",       tip: "" },
                      { h: "Calls",     tip: "Distinct calls (rc_call + rc_call_make + legacy call_logs), deduped by call_id when present. Hover a row's Calls cell for the dial-attempts breakdown." },
                      { h: "Connect %", tip: "% of calls that landed connected (or demo-booked). Source: activity_log.outcome." },
                      { h: "True Human Connect %", tip: "Of the calls classified by AI transcript analysis, the % that were genuine live human conversations (excludes voicemail/IVR/busy). Shows — until calls are classified. Source: activity_log.human_connect_verdict." },
                      { h: "Emails",    tip: "email_sent / email_received activity_log rows attributed to this rep." },
                      { h: "SMS",       tip: "rc_sms / sms_send activity_log rows attributed to this rep." },
                      { h: "Tasks",     tip: "Open / total tasks where this rep is assignee (Iter 140.9)." },
                      { h: "Updates",   tip: "Lead update + note activity rows authored by this rep (contact field changes, notes, etc.)." },
                      { h: "Demos Scheduled", tip: "Meetings booked by this rep in-window (organizer_user_id). Cancelled meetings excluded. No-shows counted — the rep did the booking work. Source: db.meetings." },
                      { h: "Demos Completed", tip: "Meetings whose status flipped to \"completed\" in-window. Source: db.meetings.status_history[]." },
                      { h: "Closed",    tip: "Leads in won/closed_won/contract/implementation stages owned by this rep." },
                      { h: "Revenue",   tip: "Sum of `value` on closed-won leads." },
                      { h: "Pipeline",  tip: "Sum of `value` on open leads. Hover for the unvalued count." },
                      { h: "Actions",   tip: "" },
                    ].map(({ h, tip }) => (
                      <th key={h} title={tip || undefined}
                          style={{ padding: "8px 10px", textAlign: "left", fontSize: 9, fontWeight: 700,
                                   color: "#86868b", textTransform: "uppercase", letterSpacing: "0.4px",
                                   cursor: tip ? "help" : "default" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{
                    repsLoading ? (
                      <tr><td colSpan={14} style={{ padding: 24, textAlign: "center", color: "#86868b", fontSize: 12 }}>
                        <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
                        Loading rep performance…
                      </td></tr>
                    ) : visibleReps.length === 0 ? (
                      <tr data-testid="rep-perf-empty"><td colSpan={14} style={{ padding: 24, textAlign: "center", color: "#86868b", fontSize: 12 }}>
                        {perfAgentIds.length ? "No selected agents have activity in this window." : "No reps with activity in this window. Invite reps via Settings → Team Members to start tracking."}
                      </td></tr>
                    ) : visibleReps.map(rep => (
                      <tr key={rep.id} style={{ borderBottom: "0.5px solid #f5f5f7" }} data-testid={`rep-row-${rep.id}`}>
                        <td style={{ padding: "10px 10px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: AP.blue + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: AP.blue }}>{rep.avatar}</div>
                            <div><div style={{ fontWeight: 600, fontSize: 12 }}>{rep.name}</div><div style={{ fontSize: 9, color: "#888" }}>{rep.role}</div></div>
                          </div>
                        </td>
                        <td style={{ padding: "8px 10px", fontWeight: 600, cursor: "help" }}
                            title={`${rep.calls ?? 0} distinct calls\n(${rep.dial_attempts ?? 0} dial-button presses logged separately)`}>
                          {rep.calls}
                        </td>
                        <td style={{ padding: "8px 10px", fontWeight: 700, color: (rep.connect_rate || 0) >= 60 ? AP.green : (rep.connect_rate || 0) >= 45 ? AP.orange : AP.red }}>{rep.connect_rate || 0}%</td>
                        <td data-testid={`rep-true-human-${rep.id}`}
                            style={{ padding: "8px 10px", fontWeight: 700, cursor: "help",
                                     color: rep.true_human_connect_rate == null ? "#94a3b8" : (rep.true_human_connect_rate >= 60 ? AP.green : rep.true_human_connect_rate >= 45 ? AP.orange : AP.red) }}
                            title={rep.true_human_connect_rate == null ? "No calls classified yet" : `${rep.true_human_yes || 0} human of ${rep.true_human_classified || 0} classified`}>
                          {rep.true_human_connect_rate == null ? "—" : `${rep.true_human_connect_rate}%`}
                        </td>
                        <td style={{ padding: "8px 10px" }}>{rep.emails}</td>
                        <td style={{ padding: "8px 10px" }}>{rep.sms || 0}</td>
                        <td style={{ padding: "8px 10px", cursor: "help" }} title={`${rep.tasks_completed || 0} completed of ${rep.tasks_total || 0} total tasks`}>{rep.tasks_open || 0}<span style={{ fontSize: 9, color: "#aaa" }}> / {rep.tasks_total || 0}</span></td>
                        <td style={{ padding: "8px 10px", cursor: "help" }} title="Lead update + note activity rows authored by this rep">{rep.contact_updates || 0}</td>
                        <td style={{ padding: "8px 10px", cursor: "pointer", fontWeight: (rep.demos_scheduled || 0) > 0 ? 600 : 400, color: (rep.demos_scheduled || 0) > 0 ? AP.teal : undefined }}
                            title={`Click to see the ${rep.demos_scheduled || 0} meeting${(rep.demos_scheduled || 0) === 1 ? "" : "s"} that fed this count.`}
                            onClick={() => (rep.demos_scheduled || 0) > 0 && openDemoDrill(rep, "scheduled")}
                            data-testid={`rep-demos-scheduled-${rep.id}`}>
                          {rep.demos_scheduled || 0}
                        </td>
                        <td style={{ padding: "8px 10px", cursor: "pointer", fontWeight: (rep.demos_completed || 0) > 0 ? 600 : 400, color: (rep.demos_completed || 0) > 0 ? AP.green : undefined }}
                            title={`Click to see the ${rep.demos_completed || 0} completed demo${(rep.demos_completed || 0) === 1 ? "" : "s"}. Show rate: ${(rep.demos_scheduled || 0) > 0 ? Math.round((rep.demos_completed || 0) / rep.demos_scheduled * 100) : 0}% (${rep.demos_completed || 0}/${rep.demos_scheduled || 0}).`}
                            onClick={() => (rep.demos_completed || 0) > 0 && openDemoDrill(rep, "completed")}
                            data-testid={`rep-demos-completed-${rep.id}`}>
                          {rep.demos_completed || 0}
                          {(rep.demos_scheduled || 0) > 0 && (
                            <div style={{ fontSize: 9, color: "#888", fontWeight: 500, marginTop: 1 }}>
                              {Math.round((rep.demos_completed || 0) / rep.demos_scheduled * 100)}% show
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px", fontWeight: 700, color: AP.green }}>{rep.closed}</td>
                        <td style={{ padding: "8px 10px", fontWeight: 700 }}>${(rep.revenue || 0).toLocaleString()}</td>
                        <td style={{ padding: "8px 10px", cursor: "help" }}
                            title={(rep.leads_without_value || 0) > 0
                              ? `${(rep.leads_without_value || 0).toLocaleString()} of ${(rep.active_leads || 0).toLocaleString()} open leads have no \`value\` set — pipeline figure understates.`
                              : "Sum of `value` field on open leads."}>
                          ${(rep.pipeline || 0).toLocaleString()}
                          {(rep.leads_without_value || 0) > 0 && (rep.active_leads || 0) > 0 && (
                            <div style={{ fontSize: 9, color: AP.orange, fontWeight: 600, marginTop: 1 }}>
                              {rep.leads_without_value.toLocaleString()} unvalued
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <div style={{ display: "flex", gap: 3 }}>
                            <button onClick={() => setDrillDown({ title: `${rep.name} — Performance Detail`, leads: [], color: AP.blue, rep })} style={{ padding: "3px 8px", fontSize: 9, borderRadius: 5, border: "0.5px solid #ddd", background: "#fff", cursor: "pointer", fontFamily: "var(--font)", fontWeight: 600 }} data-testid={`rep-drill-${rep.id}`}>View</button>
                            <button onClick={() => toast.success(`Nudge sent to ${rep.name}`)} style={{ padding: "3px 8px", fontSize: 9, borderRadius: 5, border: "0.5px solid " + AP.orange, background: AP.orange + "10", color: AP.orange, cursor: "pointer", fontFamily: "var(--font)", fontWeight: 600 }} data-testid={`rep-nudge-${rep.id}`}>Nudge</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  }</tbody>
                </table>
              </div>
            </Card>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Card title="Specialty Volume">
                <ColorKey items={(pf.specialty_data || []).map((s, i) => ({ label: s.name, color: PIE_PALETTE[i % PIE_PALETTE.length] }))} />
                {(pf.specialty_data || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={pf.specialty_data} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 9, fill: "#86868b" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#86868b" }} width={100} axisLine={false} tickLine={false} />
                      <Tooltip content={<CTooltip />} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>{(pf.specialty_data || []).map((_, i) => <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>No data</div>}
              </Card>
              <Card title="Pipeline by Stage">
                <ColorKey items={pipelineCounts.map(s => ({ label: s.label, color: s.color }))} />
                {pipelineCounts.map(s => <FunnelRow key={s.id} label={s.label} count={s.count} maxCount={maxFunnel} color={s.color} onClick={() => drillStage(s)} />)}
              </Card>
            </div>
            {(pf.impl_tracker || []).length > 0 && (
              <Card title="Implementation Tracker" onExport={() => downloadCSV(pf.impl_tracker, "implementation_tracker")} canExport={userCanExport} style={{ padding: 0 }}>
                <div style={{ overflowX: "auto", padding: "0 0 4px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead><tr style={{ borderBottom: "0.5px solid #f5f5f7" }}>
                      {["Practice", "Product", "Stage", "Timeline", "Status"].map(h => (
                        <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "#86868b", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{(pf.impl_tracker || []).map((r, i) => (
                      <tr key={i} style={{ borderBottom: "0.5px solid #f5f5f7" }}>
                        <td style={{ padding: "8px 14px", fontWeight: 600, color: "#1d1d1f" }}>{r.practice}</td>
                        <td style={{ padding: "8px 14px" }}>{r.product}</td>
                        <td style={{ padding: "8px 14px" }}><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, background: AP.blue + "12", color: AP.blue, fontWeight: 600 }}>{r.stage}</span></td>
                        <td style={{ padding: "8px 14px", color: "#86868b" }}>{r.timeline}</td>
                        <td style={{ padding: "8px 14px" }}><span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 5, background: r.status === "On Track" ? AP.green + "15" : r.status === "Delayed" ? AP.orange + "15" : AP.red + "15", color: r.status === "On Track" ? AP.green : r.status === "Delayed" ? AP.orange : AP.red, fontWeight: 650 }}>{r.status}</span></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* ═══ COMMS DASHBOARD (nested · lazy chunk per PD-12) ═══ */}
        {tab === "comms" && (
          <div data-testid="report-tab-comms-content" style={{ marginLeft: -22, marginRight: -22 }}>
            <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>Loading Comms…</div>}>
              <CommsDashboard />
            </Suspense>
          </div>
        )}

        {tab === "rep_activity" && (
          <div data-testid="report-tab-rep-activity-content" style={{ marginLeft: -22, marginRight: -22 }}>
            <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>Loading rep activity…</div>}>
              <AdminRepActivity embedded />
            </Suspense>
            <div style={{ marginLeft: 22, marginRight: 22, display: "flex", flexDirection: "column", gap: 16 }}>
              <Suspense fallback={<div style={{ padding: 20, color: "var(--text-tertiary)", fontSize: 12 }}>Loading agent performance radar…</div>}>
                <AgentPerformanceRadar days={30} />
              </Suspense>
              <Suspense fallback={<div style={{ padding: 20, color: "var(--text-tertiary)", fontSize: 12 }}>Loading AI acceptance…</div>}>
                <AiAcceptancePanel />
              </Suspense>
            </div>
          </div>
        )}

        {tab === "rc_connections" && (
          <div data-testid="report-tab-rc-connections-content">
            <RcConnectionsPanel />
          </div>
        )}

        {tab === "training" && (
          <div data-testid="report-tab-training-content">
            <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>Loading training completion…</div>}>
              <TrainingCompletionPanel />
            </Suspense>
          </div>
        )}

        {/* ═══ EXPORT & AI ═══ */}
        {userCanExport && (
          <Card title="Export Full Reports">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {REPORT_TYPES.map(rt => (
                <div key={rt.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fafafa", borderRadius: 10, border: "0.5px solid #e5e5ea" }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: rt.color + "12", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <rt.icon style={{ width: 16, height: 16, color: rt.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 650, color: "#1d1d1f" }}>{rt.label}</div>
                    <div style={{ fontSize: 9, color: "#86868b" }}>{rt.desc}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {["csv", "print"].map(fmt => (
                      <button key={fmt} onClick={() => exportReport(rt.id, fmt)} disabled={exportLoading === `${rt.id}-${fmt}`}
                        style={{ background: "#fff", border: "0.5px solid #d2d2d7", borderRadius: 8, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontFamily: "var(--font)", fontWeight: 600, color: "#86868b" }} data-testid={`export-${rt.id}-${fmt}`}>
                        {exportLoading === `${rt.id}-${fmt}` ? <Loader2 style={{ width: 10, height: 10, animation: "spin 0.8s linear infinite" }} /> :
                          fmt === "csv" ? <FileSpreadsheet style={{ width: 10, height: 10 }} /> : <Printer style={{ width: 10, height: 10 }} />}
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card title="Custom AI Report" sub="Ask Claude to analyze your CRM data">
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input value={customPrompt} onChange={e => setCustomPrompt(e.target.value)}
              placeholder="e.g., Analyze my pipeline health and suggest which deals to focus on this week..."
              className="input" style={{ flex: 1, fontSize: 12 }} data-testid="custom-report-input"
              onKeyDown={e => e.key === "Enter" && generateCustomReport()} />
            <button onClick={generateCustomReport} disabled={customLoading || !customPrompt.trim()}
              className="btn btn-primary" style={{ fontSize: 11, whiteSpace: "nowrap" }} data-testid="generate-custom-report">
              {customLoading ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />} Generate
            </button>
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {["Pipeline health check", "Which deals should I focus on?", "At-risk deals needing attention", "Monthly performance summary"].map(s => (
              <button key={s} onClick={() => setCustomPrompt(s)} style={{ padding: "3px 10px", fontSize: 9, borderRadius: 6, border: "0.5px solid #d2d2d7", background: "#fafafa", cursor: "pointer", fontFamily: "var(--font)", color: "#86868b" }}>{s}</button>
            ))}
          </div>
          {customReport && (
            <div id="custom-report-output" data-testid="custom-report-output" style={{ borderTop: "0.5px solid #e5e5ea", paddingTop: 16, marginTop: 14, scrollMarginTop: 100 }}>
              <div style={{ padding: 16, background: AP.navy, borderRadius: 12, color: "#fff", marginBottom: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Executive Summary</div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>{customReport.executive_summary}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ padding: 14, background: AP.blue + "08", borderRadius: 10, border: `0.5px solid ${AP.blue}20` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: AP.blue, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Key Metrics</div>
                  {(customReport.key_metrics || []).map((m, i) => <div key={i} style={{ display: "flex", gap: 6, fontSize: 11, color: "#1d1d1f", marginBottom: 4 }}><ArrowUpRight style={{ width: 10, height: 10, color: AP.blue, flexShrink: 0, marginTop: 2 }} /><span>{m}</span></div>)}
                </div>
                <div style={{ padding: 14, background: AP.green + "08", borderRadius: 10, border: `0.5px solid ${AP.green}20` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: AP.green, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Recommendations</div>
                  {(customReport.recommendations || []).map((r, i) => <div key={i} style={{ display: "flex", gap: 6, fontSize: 11, color: "#1d1d1f", marginBottom: 4 }}><ChevronRight style={{ width: 10, height: 10, color: AP.green, flexShrink: 0, marginTop: 2 }} /><span>{r}</span></div>)}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Drill-down Modal */}
      {drillDown && <DrillDownModal
        title={drillDown.title}
        leads={drillDown.leads}
        color={drillDown.color}
        rep={drillDown.rep}
        /* Iter 341 — pass the ACTIVE date filter down so the rep
           drill-down + AI summary honour whatever the user picked
           at the Reports page level (Today / Yesterday / preset /
           custom). Fixes the reported bug where drill always showed
           a stale 30-day window regardless of the summary filter. */
        drillDateFrom={datePreset === -1 ? customFrom : getDateRange(DATE_PRESETS[datePreset]).from}
        drillDateTo={datePreset === -1 ? customTo : getDateRange(DATE_PRESETS[datePreset]).to}
        onClose={() => setDrillDown(null)}
      />}

      {/* Meeting Drill Modal — opened by clicking a Demos Scheduled /
          Demos Completed cell on the Rep Performance table. */}
      {demoDrill && <DemoDrillModal
        rep={demoDrill.rep}
        bucket={demoDrill.bucket}
        rows={demoDrill.rows}
        loading={demoDrill.loading}
        onClose={() => setDemoDrill(null)}
      />}
    </div>
  );
}
